import { memo, useCallback, type FC } from "react";
import { InclusionSection } from "@/components/filters/UserFilters/VendorsFilter/InclusionSection";
import { ExclusionSection } from "@/components/filters/UserFilters/VendorsFilter/ExclusionSection";
import type { FilterMode } from "@/components/filters/Condition/types";
import { useCompanyFilterActions, useCompanyFilterSlice } from "../../hooks";

export const VendorsFilter: FC = memo(() => {
  const slice = useCompanyFilterSlice("vendors");
  const { setSlice } = useCompanyFilterActions();

  const handleInclusionVendorSelect = useCallback(
    (vendorIds: number[]) => {
      setSlice("vendors", { ...slice, inclusionVendorIds: vendorIds });
    },
    [setSlice, slice]
  );

  const handleExclusionVendorSelect = useCallback(
    (vendorIds: number[]) => {
      setSlice("vendors", { ...slice, excludedVendorIds: vendorIds });
    },
    [setSlice, slice]
  );

  const handleConditionChange = useCallback(
    (condition: FilterMode) => {
      setSlice("vendors", { ...slice, inclusionMethod: condition });
    },
    [setSlice, slice]
  );

  const handleFaiSelect = useCallback(
    (selectedDepartmentIds: string[], selectedRoleIds: string[]) => {
      setSlice("vendors", {
        ...slice,
        inclusionDepartmentIds: selectedDepartmentIds,
        inclusionRoleIds: selectedRoleIds,
      });
    },
    [setSlice, slice]
  );

  const handleInstallAgeChange = useCallback(
    (installAge: { min?: number | null; max?: number | null }) => {
      setSlice("vendors", {
        ...slice,
        inclusionInstallAgeMonths:
          installAge.min === undefined && installAge.max === undefined
            ? undefined
            : {
                min: installAge.min ?? undefined,
                max: installAge.max ?? undefined,
              },
      });
    },
    [setSlice, slice]
  );

  const handleProductCountChange = useCallback(
    (productCount: { min?: number | undefined; max?: number | undefined }) => {
      setSlice("vendors", {
        ...slice,
        inclusionProductCount:
          productCount.min === undefined && productCount.max === undefined
            ? undefined
            : {
                min: productCount.min ?? undefined,
                max: productCount.max ?? undefined,
              },
      });
    },
    [setSlice, slice]
  );

  return (
    <div id="vendors-filter-container">
      <InclusionSection
        condition={slice.inclusionMethod}
        selectedVendorIds={slice.inclusionVendorIds}
        onConditionChange={handleConditionChange}
        onVendorSelect={handleInclusionVendorSelect}
        selectedFaiIds={[
          ...slice.inclusionDepartmentIds,
          ...slice.inclusionRoleIds,
        ]}
        onFaiSelect={handleFaiSelect}
        installAgeMonths={slice.inclusionInstallAgeMonths}
        onInstallAgeChange={handleInstallAgeChange}
        productCount={{
          min: slice.inclusionProductCount?.min,
          max: slice.inclusionProductCount?.max,
        }}
        onProductCountChange={handleProductCountChange}
      />
      <ExclusionSection
        selectedVendorIds={slice.excludedVendorIds}
        onVendorSelect={handleExclusionVendorSelect}
      />
    </div>
  );
});

VendorsFilter.displayName = "VendorsFilter";
